import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import * as R from 'ramda'
import Modal from '../../UniformComponents/Modal'
import Select from '../form/Select'
import DataTable from '../DataTable'
import { getQuestionTitle } from '../../utils/QuestionHelper'
import Checkbox from '../form/Checkbox'
import Button from '../../UniformComponents/Button'
import { Store } from '../Modals/DownloadModal/store'
import Downloading from '../Modals/DownloadModal/downloading'

@observer
class ExportModal extends Component {
    delimiterOptions = [
        { value: 'comma', label: i18n.t('report_delimiter_comma') },
        { value: 'semiColon', label: i18n.t('report_delimiter_semicolon') },
        { value: 'tab', label: i18n.t('report_delimiter_tab') },
    ]

    exportOptions = [
        {
            label: i18n.t('exportModal.aggregated'),
            options: [
                { value: 'xlsx', label: 'Excel' },
                { value: 'pdf', label: 'PDF' },
            ],
        },
        {
            label: i18n.t('exportModal.raw'),
            options: [
                { value: 'csv', label: 'CSV' },
                { value: '3s', label: 'Triple S' },
            ],
        },
        {
            label: i18n.t('general.other'),
            options: [
                { value: 'ppt', label: 'PowerPoint' },
            ],
        },
    ]

    constructor(props) {
        super(props)
        const { reportStore } = props
        const { questionMap } = reportStore
        this.downloadStore = new Store('report', reportStore.appStore, reportStore)
        this.state = {
            exportType: undefined,
            includeQuestions: Array.from(questionMap.keys()),
            delimiter: 'comma',
            includeMetadata: false,
            exportNumericValues: false,
            downloading: false,
            includeOtherSpecify: false,
        }
    }

    handleDownload = () => {
        const {
            exportType, delimiter, exportNumericValues, includeMetadata, includeOtherSpecify,
        } = this.state
        this.downloadStore.downloadType = exportType
        this.downloadStore.downloadOptions.delimiter = delimiter
        this.downloadStore.downloadOptions.includeColumnMetadata = includeMetadata
        this.downloadStore.downloadOptions.numericExport = exportNumericValues
        this.downloadStore.downloadOptions.openEnded = includeOtherSpecify
        this.setState({ downloading: true })
    }

    getQuestionLevel = (question) => {
        const oid = R.pathOr('', ['metadata', 'oid'], question)

        if (oid === 1) return 'Explori'
        if (oid < 7) return i18n.t('company')
        if (oid === 7) return i18n.t('survey')
        return ''
    }

    getQuestionType = (question) => {
        const { questionTypeCode } = question
        if (['NF', 'RD', 'CH', 'I', 'OD', 'PC', 'RT', 'TB'].includes(questionTypeCode)) return 'Quantitative'
        return 'Qualatitive'
    }

    generateQuestionTable = () => {
        const {
            reportStore,
        } = this.props
        const { locale, appStore, questionMap } = reportStore
        const { survey } = appStore
        const { includeQuestions } = this.state
        const questions = R.values(reportStore.reportPages).map((p) => p.questions).flat(2)
            .map((question) => ({
                ...question,
                questionTitle: question.content.shortTitle ? getQuestionTitle({
                    content: question.content.shortTitle,
                    formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                    locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }).trim() : '',
                questionDescription: question.content.longTitle ? getQuestionTitle({
                    content: question.content.longTitle,
                    formContent: R.pathOr(null, ['formContent', 'longTitle'])(question),
                    locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }).trim() : '',
                questionLevel: this.getQuestionLevel(question),
                quantitative: this.getQuestionType(question) === 'Quantitative' ? 'Yes' : 'No',
                qualitative: this.getQuestionType(question) === 'Qualatitive' ? 'Yes' : 'No',
                imported: question.isDataimport ? 'Yes' : 'No',
                hidden: question.isHidden ? 'Yes' : 'No',
            }))
        const allIncluded = includeQuestions.length === [...questionMap.keys()].length
        const cols = [
            {
                name: (<Checkbox
                    checked={allIncluded}
                    onBlur={() => this.setState({ includeQuestions: allIncluded ? [] : [...questionMap.keys()] })}
                    stopPropagation
                />),
                dataTransform: (_, question) => (
                    <Checkbox
                        checked={includeQuestions.includes(question.id)}
                        onBlur={({ value }) => {
                            this.setState({
                                includeQuestions: value
                                    ? [...includeQuestions, question.id]
                                    : includeQuestions.filter((id) => id !== question.id),
                            })
                        }}
                        stopPropagation
                    />
                ),
                key: 'included',
            },
            {
                name: i18n.t('exportModal.questionTitle'),
                key: 'questionTitle',
            },
            {
                name: i18n.t('exportModal.questionDescription'),
                key: 'questionDescription',
            },
            {
                name: i18n.t('exportModal.questionLevel'),
                key: 'questionLevel',
            },
            {
                name: i18n.t('exportModal.quantitative'),
                key: 'quantitative',
            },
            {
                name: i18n.t('exportModal.openEnd'),
                key: 'qualitative',
            },
            {
                name: i18n.t('exportModal.imported'),
                key: 'imported',
            },
            {
                name: i18n.t('exportModal.hidden'),
                key: 'hidden',
            },
        ]

        return (
            <DataTable
                inMemory
                dataSet={questions}
                columns={cols}
                recordCount={questions.length}
                key="questionTable"
            />
        )
    }

    render() {
        const { reportStore } = this.props
        const { showExportModal, toggleShowExportModal } = reportStore
        const {
            exportType,
            delimiter,
            includeMetadata,
            exportNumericValues,
            downloading,
            includeQuestions,
            includeOtherSpecify,
        } = this.state
        return (
            <Modal
                show={showExportModal}
                onClose={toggleShowExportModal}
                cssClass="advancedExportModal"
            >
                {downloading
                    ? (
                        <Downloading
                            store={this.downloadStore}
                            includeQuestions={includeQuestions}
                            onClose={toggleShowExportModal}
                        />
                    ) : (
                        <div>
                            <h3>{i18n.t('export')}</h3>
                            <div className="exportType">
                                <Select
                                    name="exportType"
                                    label={`${i18n.t('exportModal.exportFormat')}:`}
                                    options={this.exportOptions}
                                    onBlur={({ value }) => this.setState({ exportType: value })}
                                    indentation
                                />
                                {exportType === 'csv'
                                    ? (
                                        <div style={{ display: 'flex', gap: '16px', width: '600px' }}>
                                            <Select
                                                style={{ width: '200px' }}
                                                name="delimiter"
                                                label={`${i18n.t('sch_upload_delimiter')}:`}
                                                options={this.delimiterOptions}
                                                onBlur={({ value }) => this.setState({ delimiter: value })}
                                                value={this.delimiterOptions.find(({ value }) => value === delimiter)}
                                            />
                                            <Checkbox
                                                label={i18n.t('exportModal.includeMetadata')}
                                                checked={includeMetadata}
                                                onBlur={() => this.setState({ includeMetadata: !includeMetadata })}
                                            />
                                            <Checkbox
                                                label={i18n.t('exportModal.exportNumericValues')}
                                                checked={exportNumericValues}
                                                onBlur={() => this.setState(
                                                    { exportNumericValues: !exportNumericValues },
                                                )}
                                            />
                                        </div>
                                    )
                                    : null}
                                {exportType === 'pdf'
                                    ? (
                                        <div style={{ display: 'flex', gap: '16px', width: '600px' }}>
                                            <Checkbox
                                                label={i18n.t('exportModal.includeOther')}
                                                checked={includeOtherSpecify}
                                                onBlur={() => this.setState(
                                                    { includeOtherSpecify: !includeOtherSpecify },
                                                )}
                                            />
                                        </div>
                                    )
                                    : null}
                            </div>
                            <div className="questionTableContainer">
                                {this.generateQuestionTable()}
                            </div>
                            <div className="footerControls">
                                <Button
                                    label={i18n.t('cancel')}
                                    cssClass="tertiary regular"
                                    onClick={toggleShowExportModal}
                                />
                                <Button
                                    label={i18n.t('submit')}
                                    cssClass="primary regular"
                                    onClick={this.handleDownload}
                                    disabled={!exportType}
                                />
                            </div>
                        </div>
                    )}
            </Modal>
        )
    }
}

export default ExportModal
